/**
 * useDocumentTypeFields — efektywne pola dodatkowe typu dokumentu dla karty dokumentu.
 *
 * Pobiera `GET /document-types/:id/fields?companyId=<firma dokumentu>` i zwraca mapę stanów pól
 * (slug → `excluded` / `optional` / `required`), którą karta przekazuje silnikowi formatek
 * (ukrywanie pól „nie dotyczy”, gwiazdki) i sprawdzeniu wymaganych pól przed wysyłką.
 *
 * Dokument bez typu → zapytanie wyłączone, `states = null` (zachowanie jak dziś: flaga
 * wymagalności pola klasy). Zmiana typu na karcie zmienia klucz zapytania i pobiera pola od nowa.
 * Gdy odczyt się nie udał (np. brak prawa podglądu słownika typów — 403), `states = null` i
 * `isError = true`: karta działa jak bez typu, a o zapisie rozstrzyga serwer.
 */

import { useMemo } from 'react';
import type { DocumentTypeFieldState } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import {
  documentTypeFieldsKey,
  fetchDocumentTypeFields,
  toTypeFieldStates,
} from './document-type-fields';

export interface UseDocumentTypeFieldsResult {
  /** Mapa stanów pól typu; `null` = brak typu albo dane niedostępne (jeszcze/wcale). */
  states: ReadonlyMap<string, DocumentTypeFieldState> | null;
  isLoading: boolean;
  isError: boolean;
}

export function useDocumentTypeFields(
  documentTypeId: string | null | undefined,
  companyId: string | null | undefined,
): UseDocumentTypeFieldsResult {
  const typeId = documentTypeId ?? null;
  const company = companyId ?? null;

  const query = useQuery({
    queryKey: documentTypeFieldsKey(typeId ?? '', company),
    queryFn: () => fetchDocumentTypeFields(typeId as string, company),
    enabled: typeId !== null,
    staleTime: 30_000,
    retry: false,
  });

  const states = useMemo(
    () =>
      typeId !== null && query.data && Array.isArray(query.data.fields)
        ? toTypeFieldStates(query.data.fields)
        : null,
    [typeId, query.data],
  );

  return { states, isLoading: typeId !== null && query.isLoading, isError: query.isError };
}
